class Utils {
  /**
   * Returns the first element that is a descendant of node that
   * matches selectors.
   *
   * @param {string} selector - A DOMString containing one or more selectors to match. This string
   *   must be a valid CSS selector string
   * @param {Object} [config] - You can pass a context to scope the element selection
   * @param {HTMLElement|Document} [context=Document] - The scope we will try and find the element
   *   in
   *
   * @returns {Element|null} The found element or null
   */
  static getElement (selector, { context = document } = {}) {
    return context.querySelector(selector)
  }

  /**
   * Returns a list of elements that are a descendant of node that
   * matches selectors.
   *
   * @param {string} selector - A DOMString containing one or more selectors to match. This string
   *   must be a valid CSS selector string
   * @param {Object} [config] - You can pass a context to scope the element selection
   * @param {HTMLElement|Document} [context=Document] - The scope we will try and find the element
   *   in
   * @param {boolean} [asArray=true] - Return the elements as an Array instead of an NodeList?
   *
   * @returns {Array<Element>|NodeListOf<Element>} The found elements or null
   */
  static getElements (selector, { context = document, asArray = true } = {}) {
    const items = context.querySelectorAll(selector)

    return asArray ? Array.from(items) : items
  }

  /**
   * Checks if the passed string contains any items in an array
   *
   * @param {string} str - The string we will be searching
   * @param {Array} [items] - A list of the items we will be checking for presence in the str
   *
   * @returns {boolean}
   */
  static doesStringContainAny (str, items = []) {
    return items.some(i => str.includes(i))
  }

  /**
   * Creates an HTMLElement
   *
   * @param {string} tag - What type of element we want this to be
   * @param {Object} [options] - A key / value pair of properties and values we want to set on the
   *   newly created element
   *
   * @returns {HTMLElement}
   */
  static createElement (tag, options = {}) {
    const el = document.createElement(tag)

    Object.entries(options).forEach(([key, value]) => {
      el.setAttribute(key, value)
    })

    return el
  }

  /**
   * Creates an array starting from zero with the length of the passed amount
   *
   * @param {number} amount - How long do we want this array to be?
   *
   * @returns {Array}
   */
  static range (amount) {
    return [...Array(amount).keys()]
  }

  /**
   * camelCase a string
   *
   * @param {string} str
   *
   * @return {string} value - formatted value
   */
  static camelize (str) {
    return str
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
        return index === 0 ? word.toLowerCase() : word.toUpperCase()
      })
      .replace(/\s+/g, '')
  }

  /**
   * A Javascript equivalent of Shopify's | img_url filter
   *
   * @param {Object} The properties we will use to create the URL
   * @param {string} src - The Shopify CDN URL of the image
   * @param {string} size - What size we want the image to be
   * @param {null|String} [crop=null] - By default the images aren't cropped but we can choose to
   *   crop them
   * @param {null|Number} [scale=null] - Images are 1x by default, you can choose to pass a
   *   different scale
   *
   * @returns {string}
   */
  static resizeImage ({ src, size, crop = null, scale = null } = {}) {
    if (size == null) {
      return src
    }

    if (size === 'master') {
      return src.replace(/http(s)?:/, '')
    }

    const match = src.match(
      /\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?/i,
    )

    if (match != null) {
      const prefix = src.split(match[0])
      const suffix = match[0]
      const scaleString = scale ? `@${scale}x` : ''
      const cropString = crop ? `_crop_${crop}` : ''

      return `${prefix[0]}_${size}${cropString}${scaleString}${suffix}`.replace(
        /http(s)?:/,
        '',
      )
    } else {
      return null
    }
  }

  /**
   * This is the default config we will use when using fetch()
   *
   * @param {string} type
   *
   * @returns {Object}
   */
  static getDefaultRequestConfig (type = 'json') {
    return {
      method: 'POST',
      headers: {
        Accept: `application/${type}`,
        'Content-Type': 'application/json',
      },
    }
  }

  /**
   * Default fetch boilerplate
   * @param {string} url - The URL we are looking to fetch
   * @param {Object} [config] - .fetch config to override the default
   *
   * @returns {Promise}
   */
  static fetchJSON (url, config = {}) {
    return fetch(
      url,
      Object.assign({}, Utils.getDefaultRequestConfig(), config),
    ).then(response => {
      if (!response.ok) {
        throw response
      }
      return response.json()
    })
  }

  /**
   * This will fetch product data from Shopify's AJAX API by handle
   *
   * @param {string} handle - The products handle
   *
   * @returns {Promise}
   */
  static fetchProductData (handle) {
    try {
      return Utils.fetchJSON(`/products/${handle}.js`)
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * This will fetch bundle anchors data from the app API for a collection
   *
   * @param {string} collectionHandle - The collection's handle
   * @param {string} shopDomain - The shop domain for the API request
   *
   * @returns {Promise}
   */
  static fetchBundleAnchors (collectionHandle, shopDomain) {
    try {
      return Utils.fetchJSON(
        `${window.theme.appSettings.abaskByBaoUrl}/api/collection/${collectionHandle}/anchors?shopDomain=${shopDomain}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        },
      )
    } catch (e) {
      console.error('Failed to fetch bundle anchors:', e)

      return Promise.resolve({ success: false, data: [] })
    }
  }

  /**
   * Gets the offsetTop of the passed element relevant to it's offsetParent
   *
   * @param {HTMLElement|Element} el - The element we'll be getting the offsetTop for
   * @param {Document|HTMLElement} [parent=HTMLBodyElement] - The offsetParent we want to get the
   *   offsetTop relative to
   * @param {number} [offsetTop=0] - The starting offsetTop. This tends to be 0 but given this is a
   *   recursive function, we need to pass it to let it build up
   *
   * @returns {number}
   */
  static getOffsetTop (el, parent = document.body, offsetTop = 0) {
    if (!isNaN(el.offsetTop)) {
      offsetTop += el.offsetTop
    }

    return el.offsetParent === parent
      ? offsetTop
      : Utils.getOffsetTop(el.offsetParent, parent, offsetTop)
  }

  static getOffsetLeft (el, parent = document.body, offsetLeft = 0) {
    if (!isNaN(el.offsetLeft)) {
      offsetLeft += el.offsetLeft
    }

    return el.offsetParent === parent
      ? offsetLeft
      : Utils.getOffsetLeft(el.offsetParent, parent, offsetLeft)
  }

  /**
   * Format money values based on your shop currency settings
   * @param  {number|string} cents - value in cents or dollar amount e.g. 300 cents
   * or 3.00 dollars
   * @param  {string} [format] - shop money_format setting
   *
   * @return {string} value - formatted value
   */
  static formatMoney (cents, format = window.theme.moneyFormat) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '')
    }
    let value = ''
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
    const formatString = format

    function formatWithDelimiters (
      number,
      precision = 2,
      thousands = ',',
      decimal = '.',
    ) {
      if (isNaN(number) || number == null) {
        return 0
      }

      number = (number / 100.0).toFixed(precision)

      const parts = number.split('.')
      const dollarsAmount = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        `$1${thousands}`,
      )
      const centsAmount = parts[1] ? decimal + parts[1] : ''

      return (dollarsAmount + centsAmount).replace('.00', '')
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2)
        break
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0)
        break
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',')
        break
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',')
        break
    }

    /** @param {string} string */
    function removeStartAndEndSlashes (string) {
      return string.replace(/^\/|\/$/g, '')
    }

    /** @type {string} */
    const currency = window.Shopify.currency.active
    /** @type {string} */
    const root = removeStartAndEndSlashes(window.Shopify.routes.root)

    const pricePrefix = (() => {
      if (currency.toLowerCase() === 'aud') {
        return 'AU'
      }

      if (['-hk', '-ca', '-sg'].some(suffix => root.endsWith(suffix))) {
        return 'US'
      }

      return ''
    })()

    return `${pricePrefix}${formatString.replace(placeholderRegex, value)}`
  }

  static getOrdinalForDay (day) {
    if (day > 3 && day < 21) return 'th'
    switch (day % 10) {
      case 1:
        return 'st'
      case 2:
        return 'nd'
      case 3:
        return 'rd'
      default:
        return 'th'
    }
  }

  /**
   * Inject variables into a translated string.
   *
   * @param {string} translation
   * @param {{ [variable: string]: any }} variables
   * @param {{ delimiters?: [string, string][] }} [options]
   *
   * @example
   * // returns Hurry! Only 2 left in stock.
   * translateString('Hurry! Only {{ count }} left in stock.', { count: 2 })
   */
  static translateString (
    translation,
    variables,
    options = {
      delimiters: [
        ['{{\\s*', '\\s*}}'],
        ['%{', '}'],
      ],
    },
  ) {
    const translatedString = Object.entries(variables).reduce(
      (baseTranslation, [key, value]) => {
        const replacePattern = options.delimiters
          .map(([start, end]) => `${start}${key}${end}`)
          .join('|')

        return baseTranslation.replace(new RegExp(replacePattern, 'g'), value)
      },
      translation,
    )

    return Utils.decodeHTML(translatedString)
  }

  /**
   * @param {Function} fn
   * @param {number} wait
   *
   * @return {Function}
   */
  static debounce (fn, wait) {
    let t

    return (...args) => {
      clearTimeout(t)
      t = setTimeout(() => fn.apply(this, args), wait)
    }
  }

  /**
   * @param {FormData} formData
   *
   * @return {Object}
   */
  static buildProperties (formData) {
    const propertyKeyValues = [...formData.entries()].filter(
      ([key, value]) => key.includes('properties[') && value !== '',
    )

    /**
     * @param {Object} properties
     * @param {[string, string]} property
     *
     * @returns {Object}
     */
    function propertyReducer (properties, [key, value]) {
      const strippedKey = key.replace('properties[', '').slice(0, -1)

      return {
        ...properties,
        [strippedKey]: value,
      }
    }

    return propertyKeyValues.reduce(
      (properties, property) => propertyReducer(properties, property),
      {},
    )
  }

  /**
   * @return {xs|sm|md|lg|xl}
   */
  static viewportSize () {
    const breakpoints = {
      xs: window.matchMedia('(max-width: 767px)').matches,
      sm: window.matchMedia('(min-width: 768px) and (max-width: 1023px)')
        .matches,
      md: window.matchMedia('(min-width: 1024px) and (max-width: 1699px)')
        .matches,
      lg: window.matchMedia('(min-width: 1700px)').matches,
    }
    return Object.keys(breakpoints).find(key => breakpoints[key])
  }

  /**
   * @returns {Boolean}
   */
  static isANumber (number) {
    return isNaN(parseInt(number, 10)) === false
  }

  /**
   * @param {Number|String} number
   *
   * @returns {Integer}
   */
  static parseInteger (number) {
    return window.BAO.Utils.isANumber(parseInt(number))
      ? parseInt(number, 10)
      : 0
  }

  /**
   * @returns {Boolean}
   */
  static isTouchDevice () {
    return !window.matchMedia('(hover: hover) and (pointer: fine)').matches
  }

  /**
   * @returns {Boolean}
   */
  static prefersReducedMotion () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  /** @param {HTMLFormElement} form */
  static serialiseForm (form) {
    const formData = new FormData(form)

    return Object.fromEntries(formData.entries())
  }

  /**
   * Returns an array of all focusable elements within a given container
   * @param {HTMLElement} container
   */
  static getFocusableElements (container) {
    const selector = `summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, input[type=radio], select:enabled, textarea:enabled, object, iframe`

    return [...container.querySelectorAll(selector)]
  }

  /**
   * @template T, U
   *
   * @param {Map<T, U>} map
   * @param {T} key
   * @param {{
   *  insert(key: T, map: Map<T, U>): U;
   *  update(existing: U, key: T, map: Map<T, U>): U;
   * }} options
   */
  static mapUpsert (map, key, options) {
    if (map.has(key)) {
      map.set(key, options.update(map.get(key), key, map))
    } else {
      map.set(key, options.insert(key, map))
    }

    return map
  }

  /**
   * @template T, U
   *
   * @param {U[]} array
   * @param {(item: U) => T} callbackFn
   */
  static groupBy (array, callbackFn) {
    return array.reduce((map, item) => {
      return Utils.mapUpsert(map, callbackFn(item), {
        insert () {
          return [item]
        },
        update (existingItems) {
          return [...existingItems, item]
        },
      })
    }, /** @type {Map<T, U[]>} */ (new Map()))
  }

  /**
   * Run a chain of promises sequentially. Useful for interacting with the Cart API in multiple calls.
   *
   * @template T
   *
   * @param {(() => Promise<T>)[]} promises
   */
  static chain (promises) {
    return promises.reduce(async (previousPromise, currentPromise) => {
      await previousPromise

      return currentPromise()
      /* eslint-disable-next-line compat/compat */
    }, Promise.resolve())
  }

  /**
   * Decode HTML entities to get the original string
   *
   * @param {string} html
   */
  static decodeHTML (html) {
    const textarea = document.createElement('textarea')
    textarea.innerHTML = html

    return textarea.value
  }

  /**
   * Remove emojis from string
   *
   * @param {string} string
   */
  static removeEmojis (string) {
    return string
      .replace(
        /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g,
        '',
      )
      .replace(/ +/g, ' ')
  }

  /** @param {number} number */
  static toOrdinal (number) {
    let ordinal = 'th'

    if (number % 10 === 1 && number % 100 !== 11) {
      ordinal = 'st'
    } else if (number % 10 === 2 && number % 100 !== 12) {
      ordinal = 'nd'
    } else if (number % 10 === 3 && number % 100 !== 13) {
      ordinal = 'rd'
    }

    return `${number}${ordinal}`
  }

  /**
   * Sleeps until a given condition is met
   *
   * @template {unknown} T
   *
   * @param {() => T} condition
   * @param {number} duration Duration in milliseconds (ms)
   *
   * @returns {Promise<T>}
   */
  static sleepUntil (condition, duration = 250) {
    /* eslint-disable-next-line compat/compat */
    return new Promise(resolve => {
      const interval = setInterval(() => {
        const result = condition()
        if (!result) {
          return
        }

        clearInterval(interval)
        resolve(result)
      }, duration)
    })
  }

  /**
   * Renders the innerHTML of a selected element
   *
   * @param {string} url The URL to render from
   * @param {string} selector Any valid css selector
   *
   * @returns {void}
   */
  static async renderInnerHTML (url, selector) {
    const element = window.BAO.Utils.getElement(selector)

    if (!element || !window.BAO?.sectionRenderer?.getInnerHtml) return

    const response = await fetch(url)
    const html = await response.text()

    const newInnerHTML = window.BAO.sectionRenderer.getInnerHtml(html, selector)

    element.innerHTML = newInnerHTML
  }

  /**
   * Format number with thousand separators (commas)
   *
   * @param {number|string} value - The number to format
   *
   * @returns {string} The formatted number with commas (e.g., 15000 -> "15,000")
   */
  static formatNumberWithCommas (value) {
    const num = Utils.parseFormattedNumber(value)
    if (isNaN(num)) return ''
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }

  /**
   * Parse formatted number string to number
   *
   * @param {string|number} value - The value to parse
   *
   * @returns {number} The parsed number
   */
  static parseFormattedNumber (value) {
    if (typeof value === 'number') return value
    return Number(String(value).replace(/,/g, ''))
  }

  /**
   * Convert a currency value based on the current exchange rate for the specified country.
   *
   * @param {number|string} value - The value to convert, can be a number or a formatted string.
   * @param {boolean} [roundUp] - If true, always round up to the next cent; if false, round to the nearest cent.
   * @param {boolean} [reverse=false] - If true, divide by the exchange rate instead of multiplying.
   *
   * @returns {number} The converted currency value, rounded to two decimal places.
   */
  static currencyExchange (value, roundUp, reverse = false) {
    if (!value) return value

    const exchangeObj = window.BAO.exchanges.find(
      obj => obj.country === window.theme?.country,
    )

    const exchangeRate = Number(exchangeObj?.exchange) || 1
    const inputNumber = Utils.parseFormattedNumber(value)

    if (isNaN(inputNumber)) return 0

    const converted = reverse
      ? inputNumber / exchangeRate
      : inputNumber * exchangeRate

    // Round to 2 decimal places. If roundUp is truthy, always round up to the next cent,
    // otherwise round to the nearest cent.
    let convertedRounded = converted

    if (roundUp === true) {
      convertedRounded = Math.ceil(converted)
    } else if (roundUp === false) {
      convertedRounded = Math.round(converted)
    }

    return parseFloat(convertedRounded.toFixed(2))
  }
}

window.BAO.Utils = Utils
